import { sleep } from "@/lib/utils";
import { ApiError } from "../errors";
import type { Api, Product, Storefront, StorefrontVerification } from "../types";
import { VERIFICATION_STEPS } from "../types";
import { products as productsApi } from "./products";
import { users as usersApi } from "./users";

// Mirrors the gateway's storefront catalog (apps/api-gateway/src/data/shops.ts):
// the addressable marketplace shops products belong to via Product.shopId.
// Distinct from the seller-onboarding records in ./sellers.ts. Offline only —
// in the wired app these come from the gateway `GET /shops`.
const BANNERS = [
	"https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=1920&h=720&fit=crop&q=80&auto=format",
	"https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=1920&h=720&fit=crop&q=80&auto=format",
	"https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1920&h=720&fit=crop&q=80&auto=format",
] as const;

const fixtures: Omit<Storefront, "productCount">[] = [
	{
		id: "shop-silver-moon",
		slug: "silver-moon",
		name: "Silver Moon",
		tagline: "Украшения ручной работы из серебра и латуни",
		description:
			"Небольшая ювелирная мастерская. Делаем серьги, подвески и браслеты вручную из серебра 925 пробы и натуральных камней.",
		heroUrl: BANNERS[2],
		rating: 4.9,
		ratingCount: 214,
		location: "Санкт-Петербург",
		since: "2019",
		status: "active",
	},
	{
		id: "shop-keramika",
		slug: "keramika-plus",
		name: "Керамика Плюс",
		tagline: "Посуда и декор из глины ручной лепки",
		description:
			"Семейная керамическая студия. Вазы, чашки и горшки — каждая вещь обжигается и расписывается вручную.",
		heroUrl: BANNERS[1],
		rating: 4.8,
		ratingCount: 168,
		location: "Москва",
		since: "2017",
		status: "active",
	},
	{
		id: "shop-natur",
		slug: "natur-tekstil",
		name: "НатурТекстиль",
		tagline: "Одежда и текстиль из натуральных тканей",
		description:
			"Шьём и вяжем из льна, хлопка и мериносовой шерсти. Спокойные силуэты, природные оттенки, долгая носка.",
		heroUrl: BANNERS[0],
		rating: 4.7,
		ratingCount: 312,
		location: "Казань",
		since: "2015",
		status: "active",
	},
	{
		id: "shop-artcraft",
		slug: "artcraft",
		name: "ArtCraft",
		tagline: "Кожаные изделия растительного дубления",
		description:
			"Мастерская кожевенных изделий. Кошельки, ремни и сумки из кожи растительного дубления с ручным тиснением.",
		heroUrl: BANNERS[2],
		rating: 4.8,
		ratingCount: 142,
		location: "Екатеринбург",
		since: "2018",
		status: "active",
	},
	{
		id: "shop-eco",
		slug: "eco-workshop",
		name: "Eco Workshop",
		tagline: "Эко-товары для дома и ухода",
		description:
			"Свечи из соевого воска, натуральное мыло, восковые салфетки и эфирные масла. Минимум упаковки, максимум пользы.",
		heroUrl: BANNERS[1],
		rating: 4.6,
		ratingCount: 256,
		location: "Краснодар",
		since: "2020",
		status: "active",
	},
	{
		id: "shop-lina",
		slug: "masterskaya-liny",
		name: "Мастерская Лины",
		tagline: "Платья и лёгкая одежда из льна",
		description:
			"Авторская мастерская Лины. Льняные платья во французском стиле — шьём небольшими партиями под сезон.",
		heroUrl: BANNERS[0],
		rating: 4.9,
		ratingCount: 98,
		location: "Сочи",
		since: "2021",
		status: "active",
	},
	{
		id: "shop-uyut",
		slug: "uyutny-dom",
		name: "Уютный дом",
		tagline: "Тёплые вещи и предметы для дома",
		description:
			"Пледы, разделочные доски и предметы интерьера из натуральных материалов — для тёплого и спокойного дома.",
		heroUrl: BANNERS[2],
		rating: 4.7,
		ratingCount: 187,
		location: "Нижний Новгород",
		since: "2016",
		status: "active",
	},
	{
		id: "shop-handmade",
		slug: "handmade-studio",
		name: "Handmade Studio",
		tagline: "Авторские украшения и декор ручной работы",
		description:
			"Студия ручного творчества. Шёлковые платки, броши из полимерной глины и панно макраме — всё в единственном экземпляре.",
		heroUrl: BANNERS[1],
		rating: 4.8,
		ratingCount: 121,
		location: "Калининград",
		since: "2019",
		status: "active",
	},
];

// productCount mirrors the gateway: published products in this shop. Pulled
// from the products mock so the two stay in sync without a second fixture set.
const publishedFor = (all: Product[], shopId: string) =>
	all.filter((p) => p.shopId === shopId && p.status === "published");

const allProducts = async (): Promise<Product[]> => {
	const page = await productsApi.list({
		status: "all",
		page: 1,
		pageSize: 100,
	});
	return page.data;
};

// MOCK-ONLY. The internal counterparty check a shop clears before trading:
// documents → legal → accounting → catalog loaded → launched. How far each
// fixture has got is keyed off its slug so the picture is stable per shop.
const VERIFICATION_PROGRESS: Record<string, number> = {
	"silver-moon": 5,
	"keramika-plus": 5,
	"natur-tekstil": 4,
	artcraft: 3,
	"eco-workshop": 2,
	"masterskaya-liny": 5,
	"uyutny-dom": 1,
	"handmade-studio": 3,
};

const VERIFIED_BY = ["Юридический отдел", "Бухгалтерия", "Мария Соколова", "Ирина Орлова"];

/** Blocking notes, shown against shops that stalled mid-check. */
const VERIFICATION_NOTES: Record<string, string> = {
	artcraft: "Не пришла карточка предприятия — запросили повторно.",
	"eco-workshop": "Реквизиты не совпадают с выпиской ЕГРИП. Ждём ответ продавца.",
	"uyutny-dom": "Приняты только сканы паспорта. Нужен договор оферты.",
	"handmade-studio": "Товарная сетка загружена частично (12 из 40 карточек).",
};

const verificationFor = (slug: string): StorefrontVerification => {
	const done = VERIFICATION_PROGRESS[slug] ?? 0;
	const steps = VERIFICATION_STEPS.map((key, i) => {
		if (i >= done) return { key, done: false };
		const at = new Date();
		// Space completed steps a few days apart, oldest first.
		at.setDate(at.getDate() - (done - i) * 3);
		return { key, done: true, at: at.toISOString(), by: VERIFIED_BY[i % VERIFIED_BY.length] };
	});

	const note = VERIFICATION_NOTES[slug];
	const commentedAt = new Date();
	commentedAt.setDate(commentedAt.getDate() - 2);

	return {
		steps,
		comments: note
			? [{ id: `cmt_${slug}`, at: commentedAt.toISOString(), author: "Мария Соколова", text: note }]
			: [],
	};
};

// Витрины, чей продавец прошёл верификацию Э7 (для демонстрации бейджа офлайн).
const VERIFIED_SHOP_IDS = new Set([
	"shop-handmade",
	"shop-silver-moon",
	"shop-natur",
]);

const hydrate = (
	s: (typeof fixtures)[number],
	all: Product[],
	names: Map<string, string>,
): Storefront => ({
	...s,
	verification: verificationFor(s.slug),
	// Бейдж «Проверенный продавец» (Э7). Офлайн — статически отмечаем несколько
	// витрин, чей продавец прошёл верификацию (напр. shop-handmade — Анна).
	verified: VERIFIED_SHOP_IDS.has(s.id),
	// The owning seller's authored shop name wins, mirroring the gateway's
	// users.shop_id join (services/shops.ts). The fixture name is seed/fallback.
	name: names.get(s.id) ?? s.name,
	productCount: publishedFor(all, s.id).length,
});

// shopId -> authored shop name, resolved from the storefront owners the same
// way the wired app joins User.shopId. Edits on the admin user page (which
// mutate User.shopName) flow through here, keeping a single source of truth.
const ownerNames = async (): Promise<Map<string, string>> => {
	const all = await usersApi.list();
	const map = new Map<string, string>();
	for (const u of all) {
		const name = u.shopName?.trim();
		if (u.shopId && name) map.set(u.shopId, name);
	}
	return map;
};

export const storefronts: Api["storefronts"] = {
	async list() {
		await sleep(150);
		const [all, names] = await Promise.all([allProducts(), ownerNames()]);
		return fixtures.map((s) => hydrate(s, all, names));
	},
	async get(slug) {
		await sleep(120);
		// Как шлюз: магазин находится и по slug, и по id.
		const s = fixtures.find((f) => f.slug === slug) ?? fixtures.find((f) => f.id === slug);
		if (!s) throw new ApiError("Storefront not found", 404, "NOT_FOUND");
		const [all, names] = await Promise.all([allProducts(), ownerNames()]);
		return hydrate(s, all, names);
	},
	async products(shopId) {
		await sleep(150);
		return publishedFor(await allProducts(), shopId);
	},
	async analytics(shopId) {
		await sleep(150);
		const s = fixtures.find((f) => f.id === shopId || f.slug === shopId);
		const today = new Date();
		const series = Array.from({ length: 30 }, (_, i) => {
			const d = new Date(today.getTime() - (29 - i) * 86_400_000);
			return { date: d.toISOString().slice(0, 10), value: 0 };
		});
		return {
			shopId,
			shopName: s?.name ?? shopId,
			shopSlug: s?.slug ?? null,
			createdAt: null,
			ordersTotal: 0,
			ordersPaid: 0,
			ordersCancelled: 0,
			gmv: 0,
			avgCheck: 0,
			commissionRate: 0.12,
			commission: 0,
			returnsCount: 0,
			returnRate: 0,
			buyersCount: 0,
			topProducts: [],
			revenueSeries: series,
			recentOrders: [],
		};
	},
	async pickupPoints(shopId: string) {
		return { shopId, enabled: false, points: [] };
	},
	async partners() {
		return { partners: [], partnerOf: [] };
	},
	async salesSummary() {
		await sleep(120);
		return fixtures.map((s) => ({
			shopId: s.id,
			shopSlug: s.slug,
			shopName: s.name,
			gmv: 0,
			orders: 0,
			lastOrderAt: null,
		}));
	},
};
